import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Search as LucideSearch, Filter as LucideFilter, Users as LucideUsers } from "lucide-react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import ReplyIcon from "@mui/icons-material/Reply";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import ExtraResourcesPanel from "./ExtraResourcesPanel";
import {
  Typography, TextField, CircularProgress, Alert, Paper,
  Box, IconButton, Avatar, List, ListItem,
  Menu, MenuItem, Fab, Chip, Tooltip,
  Popover, GlobalStyles,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InsertLink as LinksIcon,
  Email as EmailIcon,
  Assignment as ChangelogIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Send as SendIcon,
  ForwardToInbox as ResendIcon,
} from "@mui/icons-material";
import api from "../services/api";

// ── Colour palette ──────────────────────────────────────────────────────────
const PERSON_COLORS = [
  { bg: "#ccfbf1", text: "#0f766e", bar: "#14b8a6" },
  { bg: "#fef3c7", text: "#92400e", bar: "#f59e0b" },
  { bg: "#ede9fe", text: "#6d28d9", bar: "#8b5cf6" },
  { bg: "#ffe4e6", text: "#9f1239", bar: "#f43f5e" },
  { bg: "#e0f2fe", text: "#075985", bar: "#0ea5e9" },
];
function colorFor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return PERSON_COLORS[Math.abs(h) % PERSON_COLORS.length];
}
function nameInitials(name = "") {
  return name.split(" ").map((p) => p[0] || "").join("").slice(0, 2).toUpperCase() || "?";
}

const highlightMentions = (text, currentUserName) => {
  if (!text) return text;
  return text.replace(/@([\w][^\s@<]{0,40})/g, (match, name) => {
    const isMe = currentUserName && name.toLowerCase() === currentUserName.toLowerCase();
    const style = isMe
      ? "color:#fff;background:#1565c0;padding:1px 6px;border-radius:12px;font-weight:600;"
      : "color:#1565c0;background:#e3f2fd;padding:1px 6px;border-radius:12px;font-weight:600;";
    return `<span style="${style}">@${name}</span>`;
  });
};

const normalizeCommentData = (rawComment) => {
  if (!rawComment || typeof rawComment !== "object") {
    return { id: `error-${Date.now()}`, comment: "Error: Malformed comment data.", user: "System", timestamp: new Date().toISOString(), is_read: true, is_pinned: false };
  }
  let { comment = "", timestamp, ...rest } = rawComment;
  if (comment && typeof comment === "object" && !Array.isArray(comment)) {
    comment = comment.text ?? comment.body ?? JSON.stringify(comment);
  } else if (comment == null) {
    comment = "";
  } else if (typeof comment !== "string") {
    comment = String(comment);
  }
  if (timestamp) {
    if (typeof timestamp === "object") {
      if (typeof timestamp.toDate === "function") timestamp = timestamp.toDate().toISOString();
      else if (typeof timestamp.seconds === "number") {
        const d = new Date(timestamp.seconds * 1000);
        if (typeof timestamp.nanoseconds === "number") d.setMilliseconds(d.getMilliseconds() + Math.floor(timestamp.nanoseconds / 1e6));
        timestamp = d.toISOString();
      } else if (timestamp instanceof Date) timestamp = timestamp.toISOString();
      else { const d = new Date(String(timestamp)); timestamp = isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString(); }
    } else if (typeof timestamp === "number") timestamp = new Date(timestamp).toISOString();
    else if (typeof timestamp === "string") { const d = new Date(timestamp); timestamp = isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString(); }
    else timestamp = new Date().toISOString();
  } else timestamp = new Date().toISOString();
  return { ...rest, comment, timestamp };
};

// ── DiscussionImage ─────────────────────────────────────────────────────────
const DiscussionImage = ({ src, alt, ...props }) => {
  const [hovered, setHovered] = React.useState(false);
  const handleDownload = () => {
    const a = document.createElement("a"); a.href = src; a.download = alt || "image"; a.target = "_blank"; a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  return (
    <span style={{ position: "relative", display: "inline-block" }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <img src={src} alt={alt} style={{ maxWidth: "100%", borderRadius: 4, marginTop: 4, display: "block" }} {...props} />
      {hovered && (
        <IconButton size="small" onClick={handleDownload} title="Download image"
          sx={{ position: "absolute", top: 8, right: 4, bgcolor: "rgba(0,0,0,0.55)", color: "white", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" }, p: 0.5 }}>
          <FileDownloadIcon fontSize="small" />
        </IconButton>
      )}
    </span>
  );
};

const LANG_NAMES = { en: "English", ta: "Tamil", hi: "Hindi", te: "Telugu", ml: "Malayalam", kn: "Kannada", mr: "Marathi", bn: "Bengali", ur: "Urdu", pa: "Punjabi", gu: "Gujarati", or: "Odia" };

function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[880, 0], [1100, 0.18]].forEach(([freq, delay]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = freq;
      const t = ctx.currentTime + delay;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.25, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      osc.start(t); osc.stop(t + 0.4);
    });
  } catch {}
}

// ── VoiceNoteCard ───────────────────────────────────────────────────────────
const VoiceNoteCard = ({ note, isOwn, onNoteUpdated }) => {
  const isDone = note.status === "done";
  const isError = note.status === "error";
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`discussion/voice-notes/${note.id}/edit/`, { english_translation: editText }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      if (onNoteUpdated) onNoteUpdated({ ...note, english_translation: res.data.english_translation });
      setEditing(false);
    } catch { } finally { setSaving(false); }
  };
  return (
    <ListItem sx={{ justifyContent: isOwn ? "flex-end" : "flex-start", mb: 1, py: 0 }}>
      <Paper elevation={1} sx={{ p: 1.5, maxWidth: "72%", minWidth: 240, bgcolor: isOwn ? "#e8f5e9" : "#f3f4f6", borderRadius: isOwn ? "16px 4px 16px 16px" : "4px 16px 16px 16px", border: "1px solid", borderColor: isOwn ? "#a5d6a7" : "#e5e7eb" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 700, color: isOwn ? "#2e7d32" : "#1565c0" }}>{note.created_by_name}</Typography>
          {isOwn && isDone && !editing && (
            <Tooltip title="Edit transcription">
              <IconButton size="small" onClick={() => { setEditText(note.english_translation || note.transcript || ""); setEditing(true); }} sx={{ p: 0.3, color: "#6b7280" }}>
                <EditIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, bgcolor: "#2e7d32", color: "#fff", px: 0.7, py: 0.15, borderRadius: 1.5, fontSize: 10.5, fontWeight: 700 }}>
            <MicIcon sx={{ fontSize: 11 }} /> Voice Note
          </Box>
        </Box>
        {isError && <Typography variant="caption" color="error">Transcription failed. Please try again.</Typography>}
        {isDone && !editing && (
          <Typography variant="body2" sx={{ fontSize: 13, color: "#374151" }}>{note.english_translation || note.transcript || "(no speech detected)"}</Typography>
        )}
        {editing && (
          <Box sx={{ mt: 0.5 }}>
            <textarea value={editText} onChange={e => setEditText(e.target.value)} rows={3}
              style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 6, border: "1px solid #d1d5db", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
            <Box sx={{ display: "flex", gap: 1, mt: 0.5, justifyContent: "flex-end" }}>
              <IconButton size="small" onClick={() => setEditing(false)} disabled={saving}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
              <IconButton size="small" onClick={handleSave} disabled={saving}>{saving ? <CircularProgress size={12} /> : <SaveIcon sx={{ fontSize: 14 }} />}</IconButton>
            </Box>
          </Box>
        )}
        <Typography variant="caption" sx={{ display: "block", textAlign: "right", color: "#9ca3af", fontSize: 10, mt: 0.5 }}>
          {new Date(note.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </Typography>
      </Paper>
    </ListItem>
  );
};

// ── ChatInput (all complex input logic preserved) ───────────────────────────
const ChatInput = ({ comment, setComment, handleCommentSubmit, loading, fetchUsersbySegment, users, usersLoading, setSelectedMentions, quotedMessage, clearQuote, onLinksClick, onQNRClick, onAnalysisPlanClick, hasQNR, hasAnalysisPlan, onChangelogClick, segmentId }) => {
  const [recordingState, setRecordingState] = useState("idle");
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const pollRef = useRef(null);

  const injectText = (text) => {
    if (!text) return;
    const existing = (textFieldRef.current?.innerHTML || "").replace(/<br\s*\/?>/gi, "").trim();
    const newHtml = existing ? `${textFieldRef.current.innerHTML} ${text}` : text;
    if (textFieldRef.current) {
      textFieldRef.current.innerHTML = newHtml; setComment(newHtml); textFieldRef.current.focus();
      const range = document.createRange(); const sel = window.getSelection();
      range.selectNodeContents(textFieldRef.current); range.collapse(false); sel.removeAllRanges(); sel.addRange(range);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mr = new MediaRecorder(stream); mediaRecorderRef.current = mr;
      mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop()); setRecordingState("transcribing");
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        try {
          const formData = new FormData(); formData.append("audio", blob, "voice.webm"); formData.append("segment_id", segmentId);
          const res = await api.post("discussion/voice-notes/upload/", formData, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
          const noteId = res.data.id;
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = setInterval(async () => {
            try {
              const pollRes = await api.get(`discussion/voice-notes/?segment_id=${segmentId}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
              const note = (pollRes.data || []).find(n => n.id === noteId);
              if (note && (note.status === "done" || note.status === "error")) {
                clearInterval(pollRef.current); pollRef.current = null; setRecordingState("idle");
                if (note.status === "done") injectText(note.english_translation || note.transcript || "");
              }
            } catch { clearInterval(pollRef.current); pollRef.current = null; setRecordingState("idle"); }
          }, 3000);
        } catch { setRecordingState("idle"); }
      };
      mr.start(); setRecordingState("recording");
    } catch { alert("Microphone access denied. Please allow microphone access in your browser settings."); }
  };

  const stopRecording = () => { if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop(); };

  const [anchorEl, setAnchorEl] = useState(null);
  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const textFieldRef = useRef(null);
  const savedRangeRef = useRef(null);
  const saveRange = () => { const sel = window.getSelection(); if (sel && sel.rangeCount > 0) savedRangeRef.current = sel.getRangeAt(0).cloneRange(); };
  const restoreRange = () => { textFieldRef.current?.focus(); const sel = window.getSelection(); if (sel && savedRangeRef.current) { sel.removeAllRanges(); sel.addRange(savedRangeRef.current); } };
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [pastedImagePreviews, setPastedImagePreviews] = useState([]);
  const pendingImagesRef = useRef([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [triggerEmail, setTriggerEmail] = useState(false);

  useEffect(() => {
    if (comment === "") {
      if (textFieldRef.current) textFieldRef.current.innerHTML = "";
      setPastedImagePreviews([]); pendingImagesRef.current = []; setSelectedFiles([]); setTriggerEmail(false);
    }
  }, [comment]);

  const handleContextMenu = (event) => { event.preventDefault(); fetchUsersbySegment(); setAnchorEl(event.currentTarget); };
  const handleMenuClose = () => setAnchorEl(null);
  const handleMenuItemClick = (name) => {
    const el = textFieldRef.current;
    if (el) { el.focus(); document.execCommand("insertText", false, `@${name}`); setTimeout(() => setComment(el.innerHTML), 0); }
    setSelectedMentions(prev => [...prev, name]); handleMenuClose();
  };

  const handleContentInput = (e) => {
    const el = e.currentTarget; const textOnly = el.textContent.trim();
    setComment(textOnly === "" ? "" : el.innerHTML);
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { setMentionAnchor(null); setMentionQuery(""); return; }
    const range = sel.getRangeAt(0); const node = range.startContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      const textUpToCursor = node.textContent.slice(0, range.startOffset);
      const match = textUpToCursor.match(/@(\w*)$/);
      if (match) { setMentionQuery(match[1]); if (!mentionAnchor) { fetchUsersbySegment(); setMentionAnchor(textFieldRef.current); } }
      else { setMentionAnchor(null); setMentionQuery(""); }
    } else { setMentionAnchor(null); setMentionQuery(""); }
  };

  const handleMentionSelect = (name) => {
    const el = textFieldRef.current;
    if (el) {
      el.focus(); const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0); const node = range.startContainer;
        if (node.nodeType === Node.TEXT_NODE) { const offset = range.startOffset; const match = node.textContent.slice(0, offset).match(/@(\w*)$/); if (match) { range.setStart(node, offset - match[0].length); range.deleteContents(); } }
      }
      document.execCommand("insertText", false, `@${name} `); setTimeout(() => setComment(el.innerHTML), 0);
    }
    setSelectedMentions(prev => [...prev, name]); setMentionAnchor(null); setMentionQuery("");
  };

  const filteredMentionUsers = users.filter(u => { const uname = u.username || u; return uname.toLowerCase().includes(mentionQuery.toLowerCase()); });

  const handlePaste = async (event) => {
    const items = event.clipboardData?.items; if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith("image/")) {
        event.preventDefault(); const file = item.getAsFile(); if (!file) continue;
        const id = Date.now().toString(); const previewUrl = URL.createObjectURL(file);
        setPastedImagePreviews(prev => [...prev, { id, previewUrl, serverUrl: null }]); setImageUploading(true);
        try {
          const formData = new FormData(); formData.append("image", file);
          const res = await api.post("discussion/upload-image/", formData, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
          pendingImagesRef.current = [...pendingImagesRef.current, { id, serverUrl: res.data.url }];
          setPastedImagePreviews(prev => prev.map(p => p.id === id ? { ...p, serverUrl: res.data.url } : p));
        } catch { setImageError("Image upload failed. Please try again."); setPastedImagePreviews(prev => prev.filter(p => p.id !== id)); }
        finally { setImageUploading(false); } return;
      }
    }
    event.preventDefault(); const text = event.clipboardData.getData("text/plain");
    if (text) { document.execCommand("insertText", false, text); setTimeout(() => setComment(textFieldRef.current?.innerHTML || ""), 0); }
  };

  const removeImage = (id) => { setPastedImagePreviews(prev => prev.filter(p => p.id !== id)); pendingImagesRef.current = pendingImagesRef.current.filter(p => p.id !== id); };
  const handleFileSelect = (event) => { const files = Array.from(event.target.files || []); if (!files.length) return; setSelectedFiles(prev => [...prev, ...files]); setTriggerEmail(true); event.target.value = ""; };
  const removeFile = (index) => setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  const handleAttachClick = () => { if (fileInputRef.current) { fileInputRef.current.value = ""; fileInputRef.current.click(); } };
  const handleFormSubmit = async (e) => {
    const images = pendingImagesRef.current.filter(img => img.serverUrl);
    let finalComment = comment;
    if (images.length > 0) { const imageMarkdown = images.map(img => `![image](${img.serverUrl})`).join("\n"); finalComment = comment + (comment ? "\n" : "") + imageMarkdown; }
    const success = await handleCommentSubmit(e, finalComment !== comment ? finalComment : undefined, selectedFiles.length > 0 ? selectedFiles : undefined, triggerEmail);
    if (success) setSelectedFiles([]);
  };

  return (
    <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #e2e8f0", bgcolor: "#fff", flexShrink: 0 }}>
      {imageError && <Alert severity="error" onClose={() => setImageError(null)} sx={{ mb: 1, py: 0 }}>{imageError}</Alert>}
      {pastedImagePreviews.length > 0 && (
        <Box sx={{ mb: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {pastedImagePreviews.map(img => (
            <Box key={img.id} sx={{ position: "relative", display: "inline-block" }}>
              <img src={img.previewUrl} alt="preview" style={{ height: 60, maxWidth: 90, objectFit: "cover", borderRadius: 4, border: "1px solid #e5e7eb" }} />
              {!img.serverUrl && (<Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.3)", borderRadius: 1 }}><CircularProgress size={16} sx={{ color: "white" }} /></Box>)}
              {img.serverUrl && (<IconButton size="small" onClick={() => removeImage(img.id)} sx={{ position: "absolute", top: -6, right: -6, bgcolor: "white", boxShadow: 1, p: 0.2, "&:hover": { bgcolor: "#fee2e2" } }}><CloseIcon sx={{ fontSize: 10 }} /></IconButton>)}
            </Box>
          ))}
        </Box>
      )}
      {selectedFiles.length > 0 && (
        <Box sx={{ mb: 0.5, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {selectedFiles.map((file, idx) => (<Chip key={idx} label={file.name} size="small" onDelete={() => removeFile(idx)} sx={{ maxWidth: 200 }} />))}
        </Box>
      )}
      <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple hidden accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.txt,.csv,.png,.jpg,.jpeg,.gif" />

      <Box component="form" onSubmit={handleFormSubmit}
        sx={{ border: "1.5px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", bgcolor: "#f8fafc", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", "&:focus-within": { borderColor: "#0d9488", boxShadow: "0 0 0 2px rgba(13,148,136,0.10)" } }}>

        {quotedMessage && (
          <Box sx={{ px: 1.5, pt: 1, pb: 0.5, bgcolor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <ReplyIcon sx={{ fontSize: 14, color: "#6b7280" }} />
                <Typography variant="caption" color="text.secondary">Replying to <strong>{quotedMessage.user}</strong></Typography>
              </Box>
              <IconButton size="small" onClick={clearQuote} sx={{ p: 0.2 }}><CloseIcon sx={{ fontSize: 12 }} /></IconButton>
            </Box>
            <Typography variant="body2" sx={{ color: "#6b7280", pl: 1, borderLeft: "3px solid #0d9488", fontSize: 12, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
              {stripHtml(quotedMessage.comment)}
            </Typography>
          </Box>
        )}

        {(() => {
          const colorRef = React.createRef(); const hlRef = React.createRef();
          const exec = (cmd, val) => { document.execCommand(cmd, false, val || null); setTimeout(() => setComment(textFieldRef.current?.innerHTML || ""), 0); };
          const btnStyle = { border: "none", background: "transparent", cursor: "pointer", borderRadius: 5, height: 26, padding: "0 7px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#475569", fontFamily: "inherit" };
          return (
            <div style={{ display: "flex", alignItems: "center", padding: "5px 10px 0", borderBottom: "1px solid #f1f5f9", background: "#fff", gap: 2, flexWrap: "wrap" }}>
              <button title="Bold" onMouseDown={e => { e.preventDefault(); exec("bold"); }} style={{ ...btnStyle, fontWeight: 800 }}>B</button>
              <button title="Italic" onMouseDown={e => { e.preventDefault(); exec("italic"); }} style={{ ...btnStyle, fontStyle: "italic" }}>I</button>
              <button title="Underline" onMouseDown={e => { e.preventDefault(); exec("underline"); }} style={{ ...btnStyle, textDecoration: "underline" }}>U</button>
              <button title="Strikethrough" onMouseDown={e => { e.preventDefault(); exec("strikeThrough"); }} style={{ ...btnStyle, textDecoration: "line-through" }}>S</button>
              <div style={{ width: 1, height: 16, background: "#e2e8f0", margin: "0 4px" }} />
              <button title="Bullet list" onMouseDown={e => { e.preventDefault(); exec("insertUnorderedList"); }} style={{ ...btnStyle, fontSize: 11.5 }}>• List</button>
              <button title="Numbered list" onMouseDown={e => { e.preventDefault(); exec("insertOrderedList"); }} style={{ ...btnStyle, fontSize: 11.5 }}>1. List</button>
              <div style={{ width: 1, height: 16, background: "#e2e8f0", margin: "0 4px" }} />
              <Tooltip title="Text colour">
                <button onMouseDown={e => { e.preventDefault(); saveRange(); colorRef.current?.click(); }} style={{ ...btnStyle, position: "relative" }}>
                  <span style={{ fontWeight: 700 }}>A</span>
                  <input ref={colorRef} type="color" defaultValue="#e53935" style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} onChange={e => { restoreRange(); exec("foreColor", e.target.value); }} />
                </button>
              </Tooltip>
              <Tooltip title="Highlight">
                <button onMouseDown={e => { e.preventDefault(); saveRange(); hlRef.current?.click(); }} style={{ ...btnStyle, position: "relative" }}>
                  <span style={{ textDecoration: "underline", textDecorationColor: "#facc15", textDecorationThickness: 3 }}>H</span>
                  <input ref={hlRef} type="color" defaultValue="#ffff00" style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} onChange={e => { restoreRange(); exec("hiliteColor", e.target.value); }} />
                </button>
              </Tooltip>
            </div>
          );
        })()}

        <Box component="div" ref={textFieldRef} contentEditable={!imageUploading} suppressContentEditableWarning onInput={handleContentInput} onPaste={handlePaste} onContextMenu={handleContextMenu}
          sx={{ minHeight: "42px", maxHeight: "200px", overflowY: "auto", px: "14px", py: "10px", outline: "none", bgcolor: "#f8fafc", fontSize: "0.9375rem", lineHeight: 1.6, wordBreak: "break-word", color: imageUploading ? "text.disabled" : "#0f172a", "&:empty:before": { content: `attr(data-placeholder)`, color: "#94a3b8", pointerEvents: "none", display: "block" } }}
          data-placeholder={quotedMessage ? "Write your reply..." : "Write your message! (Ctrl+V to paste image)"} />

        <Box sx={{ display: "flex", alignItems: "center", px: 1, py: 0.5, borderTop: "1px solid #f1f5f9", bgcolor: "#fff", gap: 0.5 }}>
          {onLinksClick && (<Tooltip title="Test, Live & Quota Links"><IconButton size="small" onClick={onLinksClick} sx={{ color: "#1565c0", bgcolor: "#e8f1fb", "&:hover": { bgcolor: "#bbdefb" }, borderRadius: 1 }}><LinksIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>)}
          {onQNRClick && (<Tooltip title={hasQNR ? "QNR uploaded — click to view/replace" : "Upload QNR"}><IconButton size="small" onClick={onQNRClick} sx={{ color: hasQNR ? "#4a148c" : "#9ca3af", bgcolor: hasQNR ? "#f3e5f5" : "transparent", "&:hover": { bgcolor: hasQNR ? "#e1bee7" : "#f3f4f6" }, borderRadius: 1 }}><Typography sx={{ fontWeight: 700, fontSize: 10, lineHeight: 1 }}>QNR</Typography></IconButton></Tooltip>)}
          {onAnalysisPlanClick && (<Tooltip title={hasAnalysisPlan ? "Analysis Plan uploaded" : "Upload Analysis Plan"}><IconButton size="small" onClick={onAnalysisPlanClick} sx={{ color: hasAnalysisPlan ? "#bf360c" : "#9ca3af", bgcolor: hasAnalysisPlan ? "#fbe9e7" : "transparent", "&:hover": { bgcolor: hasAnalysisPlan ? "#ffccbc" : "#f3f4f6" }, borderRadius: 1 }}><Typography sx={{ fontWeight: 700, fontSize: 10, lineHeight: 1 }}>AP</Typography></IconButton></Tooltip>)}
          <Tooltip title="Attach files"><IconButton size="small" onClick={handleAttachClick} sx={{ color: "#6b7280", "&:hover": { color: "#374151", bgcolor: "#f3f4f6" }, borderRadius: 1 }}><AttachFileIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Tooltip title={triggerEmail ? "Email notification ON" : "Send email notification"}><IconButton size="small" onClick={() => setTriggerEmail(p => !p)} sx={{ color: triggerEmail ? "#1565c0" : "#9ca3af", bgcolor: triggerEmail ? "#dbeafe" : "transparent", borderRadius: 1, "&:hover": { bgcolor: triggerEmail ? "#bfdbfe" : "#f3f4f6" } }}><EmailIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Box sx={{ flex: 1 }} />
          {onChangelogClick && (<Tooltip title="Open Change Log"><IconButton size="small" onClick={onChangelogClick} sx={{ color: "#6b7280", "&:hover": { color: "#374151", bgcolor: "#f3f4f6" }, borderRadius: 1 }}><ChangelogIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>)}
          {segmentId && (
            <>
              {recordingState === "transcribing" && (<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, color: "#6b7280", fontSize: 11 }}><CircularProgress size={11} color="inherit" /><span>Transcribing…</span></Box>)}
              <Tooltip title={recordingState === "recording" ? "Stop recording" : "Record voice note"}>
                <span>
                  <IconButton size="small" disabled={recordingState === "transcribing"} onClick={recordingState === "recording" ? stopRecording : startRecording}
                    sx={{ borderRadius: "50%", p: "8px", bgcolor: recordingState === "recording" ? "#dc2626" : "#f3f4f6", color: recordingState === "recording" ? "#fff" : "#6b7280", "&:hover": { bgcolor: recordingState === "recording" ? "#b91c1c" : "#e5e7eb" } }}>
                    {recordingState === "recording" ? <StopIcon sx={{ fontSize: 18 }} /> : <MicIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <IconButton type="submit" disabled={loading || imageUploading} aria-label="send message"
            sx={{ bgcolor: "#0d9488", color: "white", borderRadius: "50%", p: "8px", "&:hover": { bgcolor: "#0f766e" }, "&.Mui-disabled": { bgcolor: "#9ca3af" } }}>
            {loading || imageUploading ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>

      <Menu open={Boolean(mentionAnchor) && (usersLoading || filteredMentionUsers.length > 0)} anchorEl={mentionAnchor} onClose={() => { setMentionAnchor(null); setMentionQuery(""); }} disableAutoFocus disableRestoreFocus anchorOrigin={{ vertical: "top", horizontal: "left" }} transformOrigin={{ vertical: "bottom", horizontal: "left" }} PaperProps={{ sx: { maxHeight: 240, minWidth: 200 } }}>
        {usersLoading ? (<MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} />Loading...</MenuItem>)
          : filteredMentionUsers.map((u, i) => { const name = u.username || u; return (<MenuItem key={i} onMouseDown={e => { e.preventDefault(); handleMentionSelect(name); }}><Avatar sx={{ width: 24, height: 24, fontSize: 12, mr: 1, bgcolor: colorFor(name).bg, color: colorFor(name).text }}>{nameInitials(name)}</Avatar>{name}</MenuItem>); })}
      </Menu>
      <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleMenuClose}>
        {usersLoading ? (<MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading users...</MenuItem>)
          : users.length > 0 ? users.map((u, i) => (<MenuItem key={i} onClick={() => handleMenuItemClick(u.username || u)}>{u.username || u}</MenuItem>))
          : (<MenuItem disabled>No users found</MenuItem>)}
      </Menu>
    </Box>
  );
};

// ── New visual components ───────────────────────────────────────────────────

function DayDivider({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "24px 0" }}>
      <div style={{ flex: 1, height: 1, backgroundColor: "#e2e8f0" }} />
      <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", color: "#64748b", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 99, padding: "4px 14px", whiteSpace: "nowrap" }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, backgroundColor: "#e2e8f0" }} />
    </div>
  );
}

const STATUS_PILL_STYLE = {
  delivered:       { bg: "#dbeafe", text: "#1d4ed8", border: "#bfdbfe" },
  received:        { bg: "#f3f4f6", text: "#374151", border: "#e5e7eb" },
  looking_into_it: { bg: "#fef3c7", text: "#92400e", border: "#fde68a" },
  in_progress:     { bg: "#dbeafe", text: "#1e40af", border: "#bfdbfe" },
  resolved:        { bg: "#d1fae5", text: "#065f46", border: "#a7f3d0" },
};

function MessageCard({ msg, isOwn, currentUserName, editingId, editText, setEditText, onSaveEdit, onCancelEdit, onReply, onEdit, onDelete, onPin, onResend, onStatusClick, onMarkRead, STATUS_CONFIG }) {
  const [hovered, setHovered] = useState(false);
  const c = colorFor(msg.user || "");
  const isEditing = editingId === msg.id;
  const statusInfo = msg.status && STATUS_CONFIG[msg.status] ? STATUS_CONFIG[msg.status] : null;
  const pillStyle = msg.status ? STATUS_PILL_STYLE[msg.status] : null;
  const formattedTime = new Date(msg.timestamp).toLocaleString("en-IN", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  const renderBody = () => {
    if (isEditing) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <TextField fullWidth size="small" multiline maxRows={4} value={editText} onChange={e => setEditText(e.target.value)} autoFocus />
          <IconButton size="small" color="primary" onClick={() => onSaveEdit(msg.id)}><SaveIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={onCancelEdit}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      );
    }
    if (msg.comment?.startsWith("[Scripting Checklist")) {
      const isWarn = msg.comment.includes("Skipped") || msg.comment.includes("Plan");
      return (
        <Box sx={{ mt: 0.5, p: 1.5, bgcolor: isWarn ? "#fff8e1" : "#e8f5e9", border: `1px solid ${isWarn ? "#ffe082" : "#a5d6a7"}`, borderLeft: `4px solid ${isWarn ? "#f9a825" : "#2e7d32"}`, borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: isWarn ? "#e65100" : "#1b5e20", display: "block", mb: 0.5 }}>{msg.comment.match(/\[([^\]]+)\]/)?.[1]}</Typography>
          <Typography component="div" variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
            <ReactMarkdown rehypePlugins={[rehypeRaw]} components={{ img: ({ node, ...props }) => <DiscussionImage {...props} /> }}>{highlightMentions(msg.comment.replace(/^\[[^\]]+\]\n/, ""), currentUserName)}</ReactMarkdown>
          </Typography>
        </Box>
      );
    }
    if (msg.comment?.match(/^\[(Test Link|Live Link|Quota Monitoring)\s/)) {
      const isLive = msg.comment.startsWith("[Live Link"); const isQuota = msg.comment.startsWith("[Quota Monitoring"); const isRemoved = msg.comment.includes("Removed");
      const hc = isQuota ? "#5d3a8e" : isLive ? "#1b5e20" : "#1565c0";
      const bg = isRemoved ? "#fafafa" : isQuota ? "#f3eeff" : isLive ? "#e8f5e9" : "#e8f1fb";
      const bdr = isRemoved ? "#ddd" : isQuota ? "#c9a8f0" : isLive ? "#a5d6a7" : "#90b8df";
      return (
        <Box sx={{ mt: 0.5, p: 1.5, bgcolor: bg, border: `1px solid ${bdr}`, borderLeft: `4px solid ${isRemoved ? "#999" : hc}`, borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: isRemoved ? "#666" : hc, display: "block", mb: 0.5 }}>{msg.comment.match(/\[([^\]]+)\]/)?.[1]}</Typography>
          <Typography component="div" variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>{msg.comment.replace(/^\[[^\]]+\]\n/, "")}</Typography>
        </Box>
      );
    }
    if (msg.comment?.match(/^\[(QNR|Analysis Plan)\s/)) {
      const isAP = msg.comment.startsWith("[Analysis Plan"); const isRemoved = msg.comment.includes("Removed");
      const hc = isAP ? "#bf360c" : "#4a148c";
      return (
        <Box sx={{ mt: 0.5, p: 1.5, bgcolor: isRemoved ? "#fafafa" : isAP ? "#fbe9e7" : "#f3e5f5", border: `1px solid ${isRemoved ? "#ddd" : isAP ? "#ffab91" : "#ce93d8"}`, borderLeft: `4px solid ${isRemoved ? "#999" : hc}`, borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: isRemoved ? "#666" : hc, display: "block", mb: 0.5 }}>{msg.comment.match(/\[([^\]]+)\]/)?.[1]}</Typography>
          <Typography component="div" variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>{msg.comment.replace(/^\[[^\]]+\]\n/, "")}</Typography>
        </Box>
      );
    }
    return (
      <Typography component="div" variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, lineHeight: 1.6, color: isOwn ? "#134e4a" : "#334155" }}>
        <ReactMarkdown rehypePlugins={[rehypeRaw]} components={{ img: ({ node, ...props }) => <DiscussionImage {...props} /> }}>{highlightMentions(msg.comment, currentUserName)}</ReactMarkdown>
      </Typography>
    );
  };

  return (
    <div
      id={`msg-${msg.id}`}
      style={{ display: "flex", alignItems: "flex-start", gap: 12, flexDirection: isOwn ? "row-reverse" : "row", cursor: !isOwn && !msg.is_read ? "pointer" : "default", maxWidth: "72%" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => !isOwn && !msg.is_read && onMarkRead(msg.id)}
    >
      {/* Avatar */}
      <div style={{ width: 36, height: 36, flexShrink: 0, borderRadius: "50%", backgroundColor: c.bg, color: c.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, boxShadow: `0 0 0 2px #fff, 0 0 0 3.5px ${c.bar}`, marginTop: 2 }}>
        {nameInitials(msg.user || "")}
      </div>

      {/* Card column */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: isOwn ? "flex-end" : "flex-start", maxWidth: "100%", minWidth: 0 }}>
        {msg.is_pinned && (
          <span style={{ fontSize: 10, fontWeight: 700, color: "#b45309", backgroundColor: "#fef3c7", border: "1px solid #fde68a", borderRadius: 99, padding: "2px 8px", marginBottom: 4 }}>
            ⭐ Pinned
          </span>
        )}

        {/* Bubble */}
        <div style={{ position: "relative", borderRadius: isOwn ? "16px 4px 16px 16px" : "4px 16px 16px 16px", backgroundColor: isOwn ? "#e6fffa" : msg.is_pinned ? "#fffbeb" : "#f1f5f9", border: isOwn ? "1px solid #99f6e4" : "none", padding: "9px 13px 8px", boxShadow: "0 1px 2px rgba(0,0,0,0.07)", width: "100%" }}>

          {/* Resend button */}
          <Tooltip title="Resend notification">
            <IconButton size="small" onClick={e => { e.stopPropagation(); onResend(msg.id); }}
              sx={{ position: "absolute", top: -14, right: 4, opacity: hovered ? 1 : 0, transition: "opacity 0.15s", bgcolor: "#ede9fe", border: "1px solid #c4b5fd", color: "#7c3aed", width: 22, height: 22, "&:hover": { bgcolor: "#ddd6fe" } }}>
              <ResendIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>

          {/* Action buttons overlay (hover) */}
          <div style={{ position: "absolute", top: 4, right: 6, display: "flex", alignItems: "center", gap: 1, opacity: hovered ? 1 : 0, transition: "opacity 0.15s", backgroundColor: isOwn ? "rgba(204,251,241,0.95)" : "rgba(241,245,249,0.92)", borderRadius: 6, padding: "1px 2px" }}>
            <Tooltip title="Reply"><IconButton size="small" onClick={e => { e.stopPropagation(); onReply(msg); }}><ReplyIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip>
            {isOwn && !isEditing && <>
              <Tooltip title="Edit"><IconButton size="small" onClick={e => { e.stopPropagation(); onEdit(msg); }}><EditIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip>
              <Tooltip title="Delete"><IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(msg.id); }}><DeleteIcon sx={{ fontSize: 13 }} color="error" /></IconButton></Tooltip>
            </>}
            <Tooltip title={msg.is_pinned ? "Unpin" : "Pin"}>
              <IconButton size="small" onClick={e => { e.stopPropagation(); onPin(msg.id); }}>
                {msg.is_pinned ? <StarIcon sx={{ fontSize: 13 }} color="warning" /> : <StarBorderIcon sx={{ fontSize: 13 }} />}
              </IconButton>
            </Tooltip>
          </div>

          {/* Sender name (others only, colored) */}
          {!isOwn && <div style={{ fontSize: 12.5, fontWeight: 700, color: c.bar, marginBottom: 3, paddingRight: 56, lineHeight: 1 }}>{msg.user}</div>}
          {isOwn && <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f766e", marginBottom: 3, paddingRight: 56, lineHeight: 1 }}>You</div>}

          {/* Quoted message */}
          {msg.quoted_message && (
            <Box onClick={e => { e.stopPropagation(); const t = document.getElementById(`msg-${msg.quoted_message.id}`); if (t) { t.scrollIntoView({ behavior: "smooth", block: "center" }); t.classList.add("msg-highlight"); setTimeout(() => t.classList.remove("msg-highlight"), 1800); } }}
              sx={{ mt: 0.5, mb: 1, p: 1, bgcolor: "rgba(0,0,0,0.06)", borderLeft: "3px solid #0d9488", borderRadius: 1, cursor: "pointer", "&:hover": { bgcolor: "rgba(13,148,136,0.1)" } }}>
              <Typography variant="caption" sx={{ color: "#0f766e", fontWeight: 600 }}>{msg.quoted_message.user}</Typography>
              <Typography variant="caption" display="block" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{stripHtml(msg.quoted_message.comment)}</Typography>
            </Box>
          )}

          {renderBody()}

          {/* Timestamp + read ticks inside bubble */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 3, marginTop: 4 }}>
            <span style={{ fontSize: 11, color: isOwn ? "#5eead4" : "#94a3b8", whiteSpace: "nowrap" }}>{formattedTime}</span>
            {isOwn && (msg.is_read
              ? <CheckCircleIcon sx={{ fontSize: 13, color: "#53bdeb" }} />
              : <CheckCircleIcon sx={{ fontSize: 13, color: "#a0aec0" }} />
            )}
          </div>
        </div>

        {/* Status pill + unread indicator below bubble */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, justifyContent: isOwn ? "flex-end" : "flex-start" }}>
          {!isOwn && !msg.is_read && <VisibilityIcon sx={{ fontSize: 12, color: "error.main" }} />}
          <button
            onClick={e => { e.stopPropagation(); onStatusClick(e, msg.id, msg.status || ""); }}
            style={{ fontSize: 11, height: 20, borderRadius: 99, padding: "0 8px", border: `1px solid ${pillStyle ? pillStyle.border : "#e2e8f0"}`, backgroundColor: pillStyle ? pillStyle.bg : "transparent", color: pillStyle ? pillStyle.text : "#94a3b8", cursor: "pointer", fontWeight: pillStyle ? 600 : 400 }}
          >
            {statusInfo ? statusInfo.label : "+ Status"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectSidebar({ segments, loading, selectedId, onSelect, query, setQuery, showAll, setShowAll }) {
  const filtered = useMemo(() => {
    let list = showAll ? segments : segments.filter(s => s.status !== "Closed" && s.status !== "Hold");
    if (query.trim()) {
      const q = query.toLowerCase().trim();
      list = list.filter(s => (s.segment_name || "").toLowerCase().includes(q) || (s.project_name || "").toLowerCase().includes(q) || String(s.id).includes(q));
    }
    return list;
  }, [segments, query, showAll]);

  const groups = useMemo(() => {
    const unread = filtered.filter(s => (s.unread_count || 0) > 0);
    const read = filtered.filter(s => !((s.unread_count || 0) > 0));
    const result = [];
    if (unread.length > 0) result.push({ label: "Unread", items: unread });
    if (read.length > 0) result.push({ label: "Active", items: read });
    return result;
  }, [filtered]);

  return (
    <aside style={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: "1px solid #e2e8f0", backgroundColor: "#fff", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", backgroundColor: "#0B2B2B", flexShrink: 0 }}>
        <div style={{ height: 28, width: 28, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#0d9488", borderRadius: 7, fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>4S</div>
        <div>
          <p style={{ fontSize: 13, fontWeight: 600, color: "#e0f2f1", margin: 0, lineHeight: 1.3, whiteSpace: "nowrap" }}>4Sight Operations</p>
          <p style={{ fontSize: 11, color: "rgba(224,242,241,0.55)", margin: 0 }}>Discussions</p>
        </div>
      </div>

      {/* Search + filter */}
      <div style={{ padding: "12px 12px 8px", flexShrink: 0 }}>
        <div style={{ position: "relative" }}>
          <LucideSearch size={14} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search projects…"
            style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 10px 7px 30px", fontSize: 13, color: "#334155", backgroundColor: "#f8fafc", outline: "none" }}
            onFocus={e => e.target.style.borderColor = "#0d9488"}
            onBlur={e => e.target.style.borderColor = "#e2e8f0"}
          />
        </div>
        <button onClick={() => setShowAll(v => !v)}
          style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 5, border: `1px solid ${showAll ? "#e2e8f0" : "#99f6e4"}`, backgroundColor: showAll ? "#fff" : "#f0fdfa", color: showAll ? "#64748b" : "#0f766e", borderRadius: 99, padding: "4px 10px", fontSize: 11, fontWeight: 500, cursor: "pointer" }}>
          <LucideFilter size={11} />
          {showAll ? "All projects" : "Active only"}
          {!showAll && <span style={{ color: "#94a3b8" }}>· excl. closed &amp; hold</span>}
        </button>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 16px" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><CircularProgress size={24} /></div>
        ) : groups.length === 0 ? (
          <p style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "24px 8px" }}>
            {query ? "No matches found." : "No projects found."}
          </p>
        ) : groups.map(group => (
          <div key={group.label}>
            <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#94a3b8", padding: "12px 8px 4px", margin: 0 }}>
              {group.label}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {group.items.map(seg => {
                const isActive = String(seg.id) === String(selectedId);
                const name = seg.segment_name || seg.project_name || `Segment #${seg.id}`;
                const sub = seg.segment_name ? (seg.project_name || "") : "";
                return (
                  <SegmentRow key={seg.id} seg={seg} isActive={isActive} name={name} sub={sub} onSelect={onSelect} />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

function SegmentRow({ seg, isActive, name, sub, onSelect }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={() => onSelect(seg.id, name)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%", textAlign: "left", borderRadius: 8, padding: "8px 10px", backgroundColor: isActive ? "#f0fdfa" : hovered ? "#f8fafc" : "transparent", border: "none", cursor: "pointer" }}
    >
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, fontWeight: 600, color: isActive ? "#0f766e" : "#1e293b" }}>{name}</span>
        {sub && <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, color: "#94a3b8" }}>{sub}</span>}
      </span>
      {(seg.unread_count || 0) > 0 && (
        <span style={{ marginLeft: 8, marginTop: 2, minWidth: 16, height: 16, padding: "0 4px", backgroundColor: "#0d9488", color: "#fff", borderRadius: 8, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {seg.unread_count}
        </span>
      )}
    </button>
  );
}

function ThreadHeader({ title, segmentId, participants, onAvatarClick, onGroupClick }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", borderBottom: "1px solid #e2e8f0", flexShrink: 0, backgroundColor: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
        <LucideUsers size={17} style={{ color: "#0d9488", flexShrink: 0 }} />
        <h2 style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title || "Project Discussion"}</h2>
        {participants.length > 0 && (
          <span style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", height: 20, minWidth: 20, padding: "0 6px", backgroundColor: "#f1f5f9", borderRadius: 10, fontSize: 11, fontWeight: 600, color: "#64748b" }}>
            {participants.length}
          </span>
        )}
        {segmentId && <ExtraResourcesPanel segmentId={segmentId} inline />}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        {participants.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ display: "flex" }}>
              {participants.slice(0, 5).map((p, idx) => {
                const name = p.name || p.username || p;
                const c = colorFor(name);
                const statuses = window.__userStatuses || {};
                const st = p.id ? (statuses[String(p.id)] || 'offline') : 'offline';
                const stColor = ({ online: '#16a34a', busy: '#f59e0b', away: '#f97316', offline: '#64748b' })[st] || '#64748b';
                return (
                  <Tooltip key={p.id || name} title={`${name} · ${st}`} arrow>
                    <div
                      onClick={() => onAvatarClick && onAvatarClick(p)}
                      style={{ position: "relative", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", backgroundColor: c.bg, color: c.text, fontSize: 10, fontWeight: 700, border: "2px solid #fff", marginLeft: idx > 0 ? -8 : 0, cursor: "pointer", transition: "transform 0.15s", zIndex: idx }}
                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.2)"}
                      onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                    >
                      {nameInitials(name)}
                      <span style={{ position: "absolute", bottom: -1, right: -1, width: 9, height: 9, borderRadius: "50%", background: stColor, border: "1.5px solid #fff", display: "block" }} />
                    </div>
                  </Tooltip>
                );
              })}
            </div>
            {participants.length > 1 && (
              <Tooltip title="Start group discussion" arrow>
                <div
                  onClick={onGroupClick}
                  style={{ width: 26, height: 26, borderRadius: "50%", background: "#f1f5f9", border: "1px dashed #94a3b8", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13, color: "#64748b" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                  onMouseLeave={e => e.currentTarget.style.background = "#f1f5f9"}
                >
                  👥
                </div>
              </Tooltip>
            )}
          </div>
        )}
        {segmentId && (
          <button onClick={() => window.open(`/changelog/${segmentId}`, "_blank")}
            style={{ fontSize: 11, color: "#64748b", border: "1px solid #e2e8f0", borderRadius: 6, padding: "3px 10px", backgroundColor: "transparent", cursor: "pointer" }}>
            📋 Change Log
          </button>
        )}
      </div>
    </div>
  );
}

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// ── Main component ──────────────────────────────────────────────────────────
const JTDiscussionPage = ({ embeddedSegmentId, embedded } = {}) => {
  const location = useLocation();
  const email = localStorage.getItem("username");

  // Segment list state (inlined from SegmentList)
  const [segments, setSegments] = useState([]);
  const [segmentsLoading, setSegmentsLoading] = useState(true);
  const [segmentsQuery, setSegmentsQuery] = useState("");
  const [showAllStatuses, setShowAllStatuses] = useState(true);

  // Chat state
  const [segmentId, setSegmentId] = useState(null);
  const [segmentName, setSegmentName] = useState(null);
  const [commentInput, setCommentInput] = useState("");
  const [comments, setComments] = useState([]);
  const [hasOlderMsgs, setHasOlderMsgs] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const lastDiscMsgIdRef = useRef(null);
  const oldestDiscMsgIdRef = useRef(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUserName, setCurrentUserName] = useState(null);
  const [currentProfileName, setCurrentProfileName] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [usersForMentions, setUsersForMentions] = useState([]);
  const [usersMentionLoading, setUsersMentionLoading] = useState(false);
  const [quotedMessage, setQuotedMessage] = useState(null);
  const [emailWarning, setEmailWarning] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);
  const [voiceNotes, setVoiceNotes] = useState([]);
  const voicePollRef = useRef(null);
  const draftPollRef = useRef(null);
  const [voiceNoteDraft, setVoiceNoteDraft] = useState(null);

  // Pinned links
  const [pinnedLinks, setPinnedLinks] = useState({ test: [], live: [], quota: [] });
  const [newLinkRows, setNewLinkRows] = useState({ test: null, live: null, quota: null });
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingLinkInput, setEditingLinkInput] = useState("");
  const [editingLinkLabel, setEditingLinkLabel] = useState("");
  const [pinnedLinkLoading, setPinnedLinkLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ test: true, live: true, quota: true });

  // Pinned attachments
  const [pinnedAttachments, setPinnedAttachments] = useState({ questionnaire: null, analysis_plan: null });
  const [attachUploading, setAttachUploading] = useState({ questionnaire: false, analysis_plan: false });
  const attachFileRefs = useRef({});

  // Popovers
  const [pinnedAnchorEl, setPinnedAnchorEl] = useState(null);
  const [qnrAnchorEl, setQnrAnchorEl] = useState(null);
  const [apAnchorEl, setApAnchorEl] = useState(null);

  // Scroll
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevCommentsLengthRef = useRef(0);
  const knownMsgIdsRef = useRef(new Set());
  const discussionPollRef = useRef(null);

  // Edit
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  // Status
  const STATUS_OPTIONS = [
    { value: "delivered",       label: "Delivered",       color: "info" },
    { value: "received",        label: "Received",        color: "secondary" },
    { value: "looking_into_it", label: "Looking Into It", color: "warning" },
    { value: "in_progress",     label: "In Progress",     color: "primary" },
    { value: "resolved",        label: "Resolved",        color: "success" },
  ];
  const STATUS_CONFIG = Object.fromEntries(STATUS_OPTIONS.map(o => [o.value, { label: o.label, color: o.color }]));
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuCommentId, setStatusMenuCommentId] = useState(null);
  const [statusMenuCurrentStatus, setStatusMenuCurrentStatus] = useState("");

  // ── Segment fetching ──────────────────────────────────────────────────────
  const fetchSegments = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) setSegmentsLoading(true);
    try {
      const em = localStorage.getItem("username");
      if (!em) return;
      const namesRes = await api.get("users/cascading_names/", { params: { email: em }, headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      const names = namesRes.data;
      if (!names || names.length === 0) { setSegments([]); return; }
      const params = new URLSearchParams();
      names.forEach(n => params.append("name", n));
      const segsRes = await api.get("/api/segments/fetch-segments-by-name/", { params, headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      setSegments(Array.isArray(segsRes.data) ? segsRes.data : []);
    } catch { } finally { if (isInitialLoad) setSegmentsLoading(false); }
  }, []);

  useEffect(() => {
    fetchSegments(true);
    const interval = setInterval(() => fetchSegments(false), 5000);
    return () => clearInterval(interval);
  }, [fetchSegments]);

  // ── Profile + chat fetching ───────────────────────────────────────────────
  useEffect(() => {
    if (!email) { setError("Email ID is missing."); setPageLoading(false); return; }
    api.get("users/profile/", { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } })
      .then(res => { setCurrentUserName(res.data.username); setCurrentProfileName(res.data.name || res.data.username || null); })
      .catch(() => setError("Failed to fetch user information."));
  }, [email]);

  const normalizeWithUsers = useCallback(async (rawList) => {
    return Promise.all(rawList.map(async raw => {
      let name = String(raw.user);
      try { const ur = await api.get("users/get_username_by_id/", { params: { user_id: raw.user }, headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); if (ur.data.username) name = ur.data.username; } catch { }
      let quotedMsg = null;
      if (raw.quoted_message) {
        let qn = String(raw.quoted_message.user);
        try { const qr = await api.get("users/get_username_by_id/", { params: { user_id: raw.quoted_message.user }, headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); if (qr.data.username) qn = qr.data.username; } catch { }
        quotedMsg = { id: raw.quoted_message.id, user: qn, comment: raw.quoted_message.comment, timestamp: raw.quoted_message.timestamp };
      }
      return normalizeCommentData({ ...raw, user: name, quoted_message: quotedMsg });
    }));
  }, []);

  const fetchComments = useCallback(async (id) => {
    if (!id) return;
    lastDiscMsgIdRef.current = null;
    oldestDiscMsgIdRef.current = null;
    setPageLoading(true); setError(null); setHasOlderMsgs(false);
    try {
      const res = await api.get(`discussion/list/?segment=${id}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      const rawList = res.data.messages || [];
      const hasMore = res.data.has_more || false;
      const normalized = await normalizeWithUsers(rawList);
      setComments(normalized);
      setHasOlderMsgs(hasMore);
      if (normalized.length > 0) {
        lastDiscMsgIdRef.current = rawList[rawList.length - 1].id;
        oldestDiscMsgIdRef.current = rawList[0].id;
      }
    } catch { setError("Failed to fetch comments. Please try again."); }
    finally { setPageLoading(false); }
  }, [normalizeWithUsers]);

  const silentPollComments = useCallback(async (id) => {
    if (!id || !lastDiscMsgIdRef.current) return;
    try {
      const res = await api.get(`discussion/list/?segment=${id}&after=${lastDiscMsgIdRef.current}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      const rawList = res.data.messages || [];
      if (rawList.length > 0) {
        playChime();
        if (Notification.permission === "granted") {
          const preview = (rawList[0].comment || "New message").slice(0, 90);
          new Notification("New Discussion Message", { body: preview, icon: "/logo192.png", silent: true });
        }
        const normalized = await normalizeWithUsers(rawList);
        setComments(prev => [...prev, ...normalized]);
        lastDiscMsgIdRef.current = rawList[rawList.length - 1].id;
        rawList.forEach(m => knownMsgIdsRef.current.add(m.id));
      }
    } catch {}
  }, [normalizeWithUsers]);

  // Request notification permission once
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Poll for new messages every 8 s — chime + notification on new messages from others
  useEffect(() => {
    if (discussionPollRef.current) clearInterval(discussionPollRef.current);
    if (!segmentId) return;
    discussionPollRef.current = setInterval(() => silentPollComments(segmentId), 8000);
    return () => { if (discussionPollRef.current) clearInterval(discussionPollRef.current); };
  }, [segmentId, silentPollComments]);

  const fetchOlderComments = useCallback(async () => {
    if (!segmentId || !oldestDiscMsgIdRef.current || loadingOlder) return;
    setLoadingOlder(true);
    const scrollEl = messagesContainerRef.current;
    const prevScrollHeight = scrollEl?.scrollHeight || 0;
    try {
      const res = await api.get(`discussion/list/?segment=${segmentId}&before=${oldestDiscMsgIdRef.current}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      const rawList = res.data.messages || [];
      if (rawList.length > 0) {
        const normalized = await normalizeWithUsers(rawList);
        setComments(prev => [...normalized, ...prev]);
        oldestDiscMsgIdRef.current = rawList[0].id;
        setHasOlderMsgs(res.data.has_more || false);
        setTimeout(() => { if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight - prevScrollHeight; }, 0);
      } else {
        setHasOlderMsgs(false);
      }
    } catch {}
    setLoadingOlder(false);
  }, [segmentId, loadingOlder, normalizeWithUsers]);

  const fetchUnreadCount = useCallback(async (id) => {
    if (!id) return;
    try { const res = await api.get(`discussion/unread-count/?segment_id=${id}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); setUnreadCount(res.data.unreadCount || 0); }
    catch { setUnreadCount(0); }
  }, []);

  const fetchVoiceNotes = useCallback(async (id) => {
    if (!id) return [];
    try { const res = await api.get(`discussion/voice-notes/?segment_id=${id}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); setVoiceNotes(res.data || []); return res.data || []; }
    catch { return []; }
  }, []);

  const fetchUsersForMentions = useCallback(async (id) => {
    const sid = id || segmentId;
    if (!sid) return;
    setUsersMentionLoading(true);
    try {
      const res = await api.get(`api/segments/get_users_by_segment/?segment_id=${sid}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      setUsersForMentions(Array.isArray(res.data) ? res.data : res.data.users || []);
    } catch { setUsersForMentions([]); }
    finally { setUsersMentionLoading(false); }
  }, [segmentId]);

  // Location-based segment selection (email links + in-app navigation)
  useEffect(() => {
    if (embedded) return; // embedded mode uses embeddedSegmentId prop instead
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get("segment_id") || location.state?.segmentId;
    if (id) {
      setSegmentId(id);
      fetchComments(id); fetchVoiceNotes(id); fetchUnreadCount(id); fetchUsersForMentions(id);
      api.get(`api/pinned-links/?segment_id=${id}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } })
        .then(res => setPinnedLinks({ test: res.data.test || [], live: res.data.live || [], quota: res.data.quota || [] })).catch(() => {});
      api.get(`api/pinned-attachments/?segment_id=${id}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } })
        .then(res => setPinnedAttachments({ questionnaire: res.data.questionnaire || null, analysis_plan: res.data.analysis_plan || null })).catch(() => {});
      api.get(`api/segments/get-segment-name-by-id/?id=${id}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } })
        .then(res => { if (res.data.segment_name) setSegmentName(res.data.segment_name); }).catch(() => {});
    } else {
      setPageLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, embedded]);

  // Embedded mode: load segment from prop
  useEffect(() => {
    if (!embedded || !embeddedSegmentId) return;
    const idStr = String(embeddedSegmentId);
    if (idStr === String(segmentId)) return;
    setSegmentId(idStr); setSegmentName(null);
    setComments([]); setVoiceNotes([]); setVoiceNoteDraft(null);
    knownMsgIdsRef.current = new Set();
    if (draftPollRef.current) { clearInterval(draftPollRef.current); draftPollRef.current = null; }
    setPageLoading(true); setError(null); setCommentInput(""); setQuotedMessage(null); setSelectedMentions([]);
    setPinnedLinks({ test: [], live: [], quota: [] }); setPinnedAttachments({ questionnaire: null, analysis_plan: null });
    fetchComments(idStr); fetchVoiceNotes(idStr); fetchUnreadCount(idStr); fetchUsersForMentions(idStr);
    api.get(`api/pinned-links/?segment_id=${idStr}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } })
      .then(res => setPinnedLinks({ test: res.data.test || [], live: res.data.live || [], quota: res.data.quota || [] })).catch(() => {});
    api.get(`api/pinned-attachments/?segment_id=${idStr}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } })
      .then(res => setPinnedAttachments({ questionnaire: res.data.questionnaire || null, analysis_plan: res.data.analysis_plan || null })).catch(() => {});
    api.get(`api/segments/get-segment-name-by-id/?id=${idStr}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } })
      .then(res => { if (res.data.segment_name) setSegmentName(res.data.segment_name); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedSegmentId, embedded]);

  useEffect(() => {
    if (comments.length > prevCommentsLengthRef.current) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    prevCommentsLengthRef.current = comments.length;
    // Keep known-IDs set current so the silent poll can detect truly new messages
    comments.forEach(c => knownMsgIdsRef.current.add(c.id));
  }, [comments]);

  const handleSidebarSegmentSelect = useCallback((id, name) => {
    if (!id || String(id) === String(segmentId)) return;
    const idStr = String(id);
    setSegmentId(idStr); setSegmentName(name || null);
    setComments([]); setVoiceNotes([]); setVoiceNoteDraft(null);
    knownMsgIdsRef.current = new Set();
    if (draftPollRef.current) { clearInterval(draftPollRef.current); draftPollRef.current = null; }
    setPageLoading(true); setError(null); setCommentInput(""); setQuotedMessage(null); setSelectedMentions([]);
    setPinnedLinks({ test: [], live: [], quota: [] }); setPinnedAttachments({ questionnaire: null, analysis_plan: null });
    fetchComments(idStr); fetchVoiceNotes(idStr); fetchUnreadCount(idStr); fetchUsersForMentions(idStr);
    api.get(`api/pinned-links/?segment_id=${idStr}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } })
      .then(res => setPinnedLinks({ test: res.data.test || [], live: res.data.live || [], quota: res.data.quota || [] })).catch(() => {});
    api.get(`api/pinned-attachments/?segment_id=${idStr}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } })
      .then(res => setPinnedAttachments({ questionnaire: res.data.questionnaire || null, analysis_plan: res.data.analysis_plan || null })).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentId, fetchComments, fetchUnreadCount, fetchVoiceNotes, fetchUsersForMentions]);

  const handleQuoteMessage = useCallback((message) => setQuotedMessage(message), []);
  const handleClearQuote = useCallback(() => setQuotedMessage(null), []);

  const handleStatusClick = useCallback((event, commentId, currentStatus) => {
    event.stopPropagation(); setStatusMenuAnchor(event.currentTarget); setStatusMenuCommentId(commentId); setStatusMenuCurrentStatus(currentStatus || "");
  }, []);

  const handleStatusSelect = useCallback(async (newStatus) => {
    const commentId = statusMenuCommentId; const currentStatus = statusMenuCurrentStatus;
    setStatusMenuAnchor(null); setStatusMenuCommentId(null);
    if (newStatus === currentStatus) return;
    setComments(prev => prev.map(c => c.id === commentId ? { ...c, status: newStatus } : c));
    try { await api.post("discussion/set_status/", { discussion_id: commentId, status: newStatus }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); }
    catch { setComments(prev => prev.map(c => c.id === commentId ? { ...c, status: currentStatus } : c)); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusMenuCommentId, statusMenuCurrentStatus]);

  const handleStartEdit = useCallback((comment) => { setEditingId(comment.id); setEditText(comment.comment); }, []);
  const handleCancelEdit = useCallback(() => { setEditingId(null); setEditText(""); }, []);
  const handleSaveEdit = useCallback(async (commentId) => {
    if (!editText.trim()) return;
    try {
      const res = await api.post("discussion/edit/", { discussion_id: commentId, comment: editText.trim() }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, comment: res.data.comment.comment } : c));
      setEditingId(null); setEditText("");
    } catch (err) { setError(err.response?.data?.error || "Failed to edit message."); }
  }, [editText]);

  const handleDeleteMessage = useCallback(async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    try { await api.post("discussion/delete/", { discussion_id: commentId }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); setComments(prev => prev.filter(c => c.id !== commentId)); }
    catch (err) { setError(err.response?.data?.error || "Failed to delete message."); }
  }, []);

  const handleResendNotification = useCallback(async (discussionId) => {
    try {
      const res = await api.post("discussion/resend-notification/", { discussion_id: discussionId }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      setEmailSuccess(`Notification sent to: ${(res.data.sent_to || []).join(", ")}`);
      setTimeout(() => setEmailSuccess(null), 5000);
    } catch (err) { setError(err.response?.data?.error || "Failed to resend notification."); }
  }, []);

  const handleCommentSubmit = useCallback(async (e, overrideComment, files, triggerEmail) => {
    e.preventDefault();
    const finalComment = overrideComment !== undefined ? overrideComment : commentInput;
    if (!currentUserName || !finalComment.trim() || !segmentId) return;
    setSubmitLoading(true); setError(null);
    try {
      let res;
      if (files && files.length > 0) {
        const formData = new FormData();
        formData.append("segment", segmentId); formData.append("userId", email); formData.append("comment", finalComment); formData.append("send_email", triggerEmail ? "true" : "false");
        if (quotedMessage) formData.append("quoted_message_id", quotedMessage.id);
        if (selectedMentions.length > 0) formData.append("mentioned_names", JSON.stringify(selectedMentions));
        files.forEach(f => formData.append("attachments", f));
        res = await api.post("discussion/create/", formData, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      } else {
        const payload = { segment: segmentId, userId: email, comment: finalComment, send_email: triggerEmail || false };
        if (quotedMessage) payload.quoted_message_id = quotedMessage.id;
        if (selectedMentions.length > 0) payload.mentioned_names = selectedMentions;
        res = await api.post("discussion/create/", payload, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      }
      const entity = res.data.comment;
      if (!entity) throw new Error("Unexpected response");
      setComments(prev => [...prev, normalizeCommentData({ ...entity, user: currentUserName, is_read: true, quoted_message: quotedMessage })]);
      setCommentInput(""); setSelectedMentions([]); setQuotedMessage(null);
      fetchUnreadCount(segmentId);
      if (res.data.email_warning) { setEmailWarning(res.data.email_warning); setTimeout(() => setEmailWarning(null), 8000); }
      else if (res.data.email_sent && res.data.email_sent_to?.length > 0) { setEmailSuccess(`Email sent to: ${res.data.email_sent_to.join(", ")}`); setTimeout(() => setEmailSuccess(null), 5000); }
      return true;
    } catch (err) { setError(err.response?.data?.detail || "Failed to post comment."); }
    finally { setSubmitLoading(false); }
  }, [commentInput, currentUserName, segmentId, email, selectedMentions, quotedMessage, fetchUnreadCount]);

  const markCommentAsRead = useCallback(async (commentId) => {
    const idx = comments.findIndex(c => c.id === commentId);
    if (idx < 0 || comments[idx].is_read) return;
    setComments(comments.map(c => c.id === commentId ? { ...c, is_read: true } : c));
    setUnreadCount(prev => Math.max(prev - 1, 0));
    // Immediately reflect in sidebar badge
    setSegments(prev => prev.map(s => s.id === segmentId
      ? { ...s, unread_count: Math.max((s.unread_count || 0) - 1, 0) }
      : s
    ));
    try { await api.post("discussion/mark_as_read/", { segment_id: segmentId, discussion_id: commentId }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); }
    catch {
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, is_read: false } : c));
      setUnreadCount(p => p + 1);
      setSegments(prev => prev.map(s => s.id === segmentId ? { ...s, unread_count: (s.unread_count || 0) + 1 } : s));
    }
  }, [comments, segmentId, setSegments]);

  const handlePin = useCallback(async (commentId) => {
    const target = comments.find(c => c.id === commentId); if (!target) return;
    const newStatus = !target.is_pinned; const previous = [...comments];
    setComments(comments.map(c => ({ ...c, is_pinned: c.id === commentId ? newStatus : false })));
    try { await api.post("discussion/pin/", { segment_id: segmentId, discussion_id: newStatus ? commentId : null }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); }
    catch (err) { if (err.response?.status !== 404) { setComments(previous); setError("Failed to pin comment."); } }
  }, [comments, segmentId]);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const up = container.scrollHeight - container.scrollTop - container.clientHeight > 200;
    if (up !== showScrollButton) setShowScrollButton(up);
    if (container.scrollTop < 80 && hasOlderMsgs && !loadingOlder) fetchOlderComments();
  };

  const handleAttachmentUpload = async (attachType, file) => {
    setAttachUploading(prev => ({ ...prev, [attachType]: true }));
    try {
      const formData = new FormData(); formData.append("segment_id", segmentId); formData.append("attachment_type", attachType); formData.append("file", file);
      const res = await api.post("api/pinned-attachments/upload/", formData, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      setPinnedAttachments(prev => ({ ...prev, [attachType]: res.data })); fetchComments(segmentId);
    } catch { } finally { setAttachUploading(prev => ({ ...prev, [attachType]: false })); }
  };

  const handleAttachmentDelete = async (attachType) => {
    if (!window.confirm("Remove this attachment?")) return;
    try { await api.delete(`api/pinned-attachments/delete/?segment_id=${segmentId}&attachment_type=${attachType}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); setPinnedAttachments(prev => ({ ...prev, [attachType]: null })); fetchComments(segmentId); } catch { }
  };

  const addLinkRow = (key) => setNewLinkRows(prev => ({ ...prev, [key]: prev[key] ? [...prev[key], { label: "", url: "" }] : [{ label: "", url: "" }] }));
  const removeLinkRow = (key, idx) => setNewLinkRows(prev => { const rows = prev[key].filter((_, i) => i !== idx); return { ...prev, [key]: rows.length > 0 ? rows : null }; });
  const updateLinkRow = (key, idx, field, val) => setNewLinkRows(prev => ({ ...prev, [key]: prev[key].map((r, i) => i === idx ? { ...r, [field]: val } : r) }));

  const handlePinnedLinkAddAll = async (linkType) => {
    const rows = (newLinkRows[linkType] || []).filter(r => r.url.trim()); if (!rows.length) return;
    setPinnedLinkLoading(true);
    try {
      const results = [];
      for (const row of rows) { const res = await api.post("api/pinned-links/add/", { segment_id: segmentId, link_type: linkType, url: row.url.trim(), label: row.label.trim() || undefined }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); results.push(res.data); }
      setPinnedLinks(prev => ({ ...prev, [linkType]: [...prev[linkType], ...results] })); setNewLinkRows(prev => ({ ...prev, [linkType]: null }));
      api.post("api/pinned-links/notify/", { segment_id: segmentId, link_type: linkType }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }).catch(() => {});
      fetchComments(segmentId);
    } catch (err) { alert(err?.response?.data?.error || "Failed to save link. Please try again."); } finally { setPinnedLinkLoading(false); }
  };

  const handlePinnedLinkUpdate = async (linkId, linkType) => {
    const url = editingLinkInput.trim(); if (!url) return;
    setPinnedLinkLoading(true);
    try {
      const res = await api.patch("api/pinned-links/update/", { link_id: linkId, url, label: editingLinkLabel.trim() || undefined }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } });
      setPinnedLinks(prev => ({ ...prev, [linkType]: prev[linkType].map(l => l.id === linkId ? res.data : l) }));
      setEditingLinkId(null); setEditingLinkInput(""); setEditingLinkLabel("");
      api.post("api/pinned-links/notify/", { segment_id: segmentId, link_type: linkType }, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }).catch(() => {});
      fetchComments(segmentId);
    } catch { } finally { setPinnedLinkLoading(false); }
  };

  const handlePinnedLinkDelete = async (linkId, linkType) => {
    try { await api.delete(`api/pinned-links/delete/?link_id=${linkId}`, { headers: { Authorization: `Token ${localStorage.getItem("Token")}` } }); setPinnedLinks(prev => ({ ...prev, [linkType]: prev[linkType].filter(l => l.id !== linkId) })); fetchComments(segmentId); } catch { }
  };

  const allItems = useMemo(() => {
    const vn = voiceNotes.filter(v => v.status === "done").map(v => ({ ...v, _type: "voice_note", timestamp: v.created_at }));
    const cm = comments.map(c => ({ ...c, _type: "comment" }));
    return [...vn, ...cm].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [comments, voiceNotes]);

  const participantNames = useMemo(() => usersForMentions.map(u => u.username || u), [usersForMentions]);
  const participantUsers = useMemo(() => usersForMentions, [usersForMentions]);

  const handleAvatarClick = (user) => {
    window.dispatchEvent(new CustomEvent('openDM', { detail: user }));
  };

  const handleGroupClick = () => {
    window.dispatchEvent(new CustomEvent('openDM', { detail: null }));
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", height: embedded ? "100%" : "calc(100vh - 100px)", overflow: "hidden" }}>
      <GlobalStyles styles={{ ".msg-highlight": { background: "rgba(13,148,136,0.14) !important", borderRadius: "8px", transition: "background 0.6s" } }} />

      {!embedded && (
        <ProjectSidebar
          segments={segments}
          loading={segmentsLoading}
          selectedId={segmentId}
          onSelect={handleSidebarSegmentSelect}
          query={segmentsQuery}
          setQuery={setSegmentsQuery}
          showAll={showAllStatuses}
          setShowAll={setShowAllStatuses}
        />
      )}

      {/* Main thread area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", minWidth: 0 }}>
        <ThreadHeader
          title={segmentName || (segmentId ? `Segment ${segmentId}` : "Project Discussion")}
          segmentId={segmentId}
          participants={participantUsers}
          onAvatarClick={handleAvatarClick}
          onGroupClick={handleGroupClick}
        />

        {/* Message list */}
        <div ref={messagesContainerRef} onScroll={handleScroll}
          style={{ flex: 1, overflowY: "auto", padding: "16px 32px 16px", backgroundColor: "#f8fafc", position: "relative" }}>

          {!segmentId ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 8, color: "#94a3b8" }}>
              <LucideUsers size={40} style={{ opacity: 0.3 }} />
              <p style={{ fontSize: 15, fontWeight: 500, margin: 0 }}>Select a project to view its discussion</p>
              <p style={{ fontSize: 13, margin: 0 }}>Choose from the list on the left.</p>
            </div>
          ) : (
            <>
              {pageLoading && <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><CircularProgress /></div>}
              {error && !pageLoading && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, mt: 2 }}>{error}</Alert>}
              {!pageLoading && hasOlderMsgs && (
                <div style={{ textAlign: "center", marginBottom: 12 }}>
                  {loadingOlder
                    ? <CircularProgress size={18} />
                    : <button onClick={fetchOlderComments} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 20, padding: "4px 16px", fontSize: 12, color: "#64748b", cursor: "pointer" }}>↑ Load older messages</button>
                  }
                </div>
              )}
              {!pageLoading && allItems.length === 0 && (
                <p style={{ textAlign: "center", color: "#94a3b8", marginTop: 40, fontSize: 14 }}>No messages yet. Be the first to comment!</p>
              )}
              <div style={{ maxWidth: 920, margin: "0 auto" }}>
                {allItems.map((item, index) => {
                  const currentDate = new Date(item.timestamp).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
                  const previousDate = index > 0 ? new Date(allItems[index - 1].timestamp).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : null;
                  const showDivider = currentDate !== previousDate;

                  if (item._type === "voice_note") {
                    return (
                      <React.Fragment key={`vn-${item.id}`}>
                        {showDivider && <DayDivider label={currentDate} />}
                        <div style={{ paddingBottom: 8 }}>
                          <List sx={{ py: 0 }}>
                            <VoiceNoteCard note={item} isOwn={item.created_by_name === currentProfileName} onNoteUpdated={note => setVoiceNotes(prev => prev.map(n => n.id === note.id ? note : n))} />
                          </List>
                        </div>
                      </React.Fragment>
                    );
                  }

                  const isOwn = item.user === currentUserName;
                  return (
                    <React.Fragment key={item.id}>
                      {showDivider && <DayDivider label={currentDate} />}
                      <div style={{ paddingBottom: 8, display: "flex", justifyContent: isOwn ? "flex-end" : "flex-start" }}>
                        <MessageCard
                          msg={item}
                          isOwn={isOwn}
                          currentUserName={currentUserName}
                          editingId={editingId}
                          editText={editText}
                          setEditText={setEditText}
                          onSaveEdit={handleSaveEdit}
                          onCancelEdit={handleCancelEdit}
                          onReply={handleQuoteMessage}
                          onEdit={handleStartEdit}
                          onDelete={handleDeleteMessage}
                          onPin={handlePin}
                          onResend={handleResendNotification}
                          onStatusClick={handleStatusClick}
                          onMarkRead={markCommentAsRead}
                          STATUS_CONFIG={STATUS_CONFIG}
                        />
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              <div ref={messagesEndRef} />
              {showScrollButton && (
                <Fab color="primary" size="small" onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })} sx={{ position: "absolute", bottom: 16, right: 16 }}>
                  <KeyboardArrowDownIcon />
                </Fab>
              )}
            </>
          )}
        </div>

        {emailWarning && <Alert severity="warning" onClose={() => setEmailWarning(null)} sx={{ mx: 2, mb: 1 }}>{emailWarning}</Alert>}
        {emailSuccess && <Alert severity="success" onClose={() => setEmailSuccess(null)} sx={{ mx: 2, mb: 1 }}>{emailSuccess}</Alert>}

        {/* Hidden file inputs for attachments */}
        {["questionnaire", "analysis_plan"].map(key => (
          <input key={key} type="file" ref={el => { attachFileRefs.current[key] = el; }} style={{ display: "none" }}
            onChange={e => { if (e.target.files?.[0]) handleAttachmentUpload(key, e.target.files[0]); e.target.value = ""; }} />
        ))}

        <ChatInput
          comment={commentInput}
          setComment={setCommentInput}
          handleCommentSubmit={handleCommentSubmit}
          loading={submitLoading}
          fetchUsersbySegment={() => fetchUsersForMentions()}
          users={usersForMentions}
          usersLoading={usersMentionLoading}
          setSelectedMentions={setSelectedMentions}
          quotedMessage={quotedMessage}
          clearQuote={handleClearQuote}
          onLinksClick={segmentId ? e => setPinnedAnchorEl(e.currentTarget) : undefined}
          onQNRClick={segmentId ? e => setQnrAnchorEl(e.currentTarget) : undefined}
          onAnalysisPlanClick={segmentId ? e => setApAnchorEl(e.currentTarget) : undefined}
          hasQNR={Boolean(pinnedAttachments.questionnaire)}
          hasAnalysisPlan={Boolean(pinnedAttachments.analysis_plan)}
          onChangelogClick={segmentId ? () => window.open(`/changelog/${segmentId}`, "_blank") : undefined}
          segmentId={segmentId}
        />
      </div>

      {/* Pinned links popover */}
      <Popover open={Boolean(pinnedAnchorEl)} anchorEl={pinnedAnchorEl} onClose={() => setPinnedAnchorEl(null)} anchorOrigin={{ vertical: "top", horizontal: "left" }} transformOrigin={{ vertical: "bottom", horizontal: "left" }} disableRestoreFocus PaperProps={{ sx: { width: 400, maxHeight: 480, overflowY: "auto", p: 1.5, display: "flex", flexDirection: "column", gap: 1 } }}>
        {[{ key: "test", label: "Test Links", icon: "🔗", color: "#1565c0", bg: "#e8f1fb", border: "#90b8df" }, { key: "live", label: "Live Links", icon: "🟢", color: "#1b5e20", bg: "#e8f5e9", border: "#a5d6a7" }, { key: "quota", label: "Quota Monitoring", icon: "📊", color: "#5d3a8e", bg: "#f3eeff", border: "#c9a8f0" }].map(({ key, label, icon, color, bg, border }) => {
          const links = pinnedLinks[key] || []; const rows = newLinkRows[key]; const isAdding = rows !== null; const isExpanded = expandedGroups[key]; const hasContent = links.length > 0 || isAdding;
          return (
            <Box key={key} sx={{ border: `1px solid ${border}`, borderRadius: "7px", background: bg, overflow: "hidden" }}>
              <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.4, cursor: "pointer", borderBottom: isExpanded && hasContent ? `1px solid ${border}` : "none" }} onClick={() => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }))}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color, flex: 1, letterSpacing: 0.3 }}>{icon} {label}{links.length > 0 ? ` (${links.length})` : ""}</Typography>
                <Tooltip title={`Add ${label.replace("Links", "Link")}`}><IconButton size="small" sx={{ p: 0.3, color }} onClick={e => { e.stopPropagation(); addLinkRow(key); setEditingLinkId(null); setExpandedGroups(prev => ({ ...prev, [key]: true })); }}><Typography sx={{ fontSize: 16, lineHeight: 1, fontWeight: 700 }}>+</Typography></IconButton></Tooltip>
                <IconButton size="small" sx={{ p: 0.3, color }} onClick={e => { e.stopPropagation(); setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] })); }}>{isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}</IconButton>
              </Box>
              {isExpanded && (
                <>
                  {links.length > 0 && (<Box sx={{ maxHeight: 120, overflowY: "auto" }}>{links.map((lnk, i) => (<Box key={lnk.id}>{editingLinkId === lnk.id ? (<Box sx={{ px: 1.5, py: 0.5, borderBottom: i < links.length - 1 || isAdding ? `1px solid ${border}` : "none" }}><Box sx={{ display: "flex", gap: 0.5, mb: 0.4 }}><TextField size="small" value={editingLinkLabel} onChange={e => setEditingLinkLabel(e.target.value)} placeholder="Label" sx={{ flex: "0 0 40%", "& .MuiInputBase-input": { fontSize: 12, py: 0.3 } }} disabled={pinnedLinkLoading} /><TextField size="small" autoFocus value={editingLinkInput} onChange={e => setEditingLinkInput(e.target.value)} onKeyDown={e => { if (e.key === "Escape") { setEditingLinkId(null); setEditingLinkInput(""); setEditingLinkLabel(""); } }} placeholder="URL" sx={{ flex: 1, "& .MuiInputBase-input": { fontSize: 12, py: 0.3 } }} disabled={pinnedLinkLoading} /></Box><Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}><Tooltip title="Cancel"><IconButton size="small" onClick={() => { setEditingLinkId(null); setEditingLinkInput(""); setEditingLinkLabel(""); }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip><IconButton size="small" sx={{ bgcolor: color, color: "#fff", borderRadius: 1, px: 1, py: 0.3 }} onClick={() => handlePinnedLinkUpdate(lnk.id, key)} disabled={pinnedLinkLoading}><SaveIcon sx={{ fontSize: 13, mr: 0.3 }} /><Typography sx={{ fontSize: 11, fontWeight: 700 }}>Save</Typography></IconButton></Box></Box>) : (<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.25, borderBottom: i < links.length - 1 ? `1px solid ${border}` : "none" }}><Box sx={{ flex: 1, overflow: "hidden" }}>{lnk.label ? <Typography component="a" href={lnk.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 11, color, fontWeight: 700, textDecoration: "underline", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{lnk.label}</Typography> : <Typography component="a" href={lnk.url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 11, color, textDecoration: "underline", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{lnk.url}</Typography>}</Box><Tooltip title="Edit"><IconButton size="small" sx={{ color: "#666", p: 0.3 }} onClick={() => { setEditingLinkId(lnk.id); setEditingLinkInput(lnk.url); setEditingLinkLabel(lnk.label || ""); setNewLinkRows(prev => ({ ...prev, [key]: null })); }}><EditIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip><Tooltip title="Remove"><IconButton size="small" sx={{ color: "#c62828", p: 0.3 }} onClick={() => handlePinnedLinkDelete(lnk.id, key)}><DeleteIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip></Box>)}</Box>))}</Box>)}
                  {isAdding && (<Box sx={{ px: 1.5, py: 0.5, borderTop: links.length > 0 ? `1px solid ${border}` : "none" }}>{rows.map((row, idx) => (<Box key={idx} sx={{ display: "flex", gap: 0.5, mb: 0.4, alignItems: "center" }}><TextField size="small" value={row.label} onChange={e => updateLinkRow(key, idx, "label", e.target.value)} placeholder="Label" sx={{ flex: "0 0 40%", "& .MuiInputBase-input": { fontSize: 12, py: 0.3 } }} disabled={pinnedLinkLoading} /><TextField size="small" value={row.url} onChange={e => updateLinkRow(key, idx, "url", e.target.value)} autoFocus={idx === rows.length - 1} onKeyDown={e => { if (e.key === "Escape") setNewLinkRows(prev => ({ ...prev, [key]: null })); }} placeholder={`Paste ${key} link URL…`} sx={{ flex: 1, "& .MuiInputBase-input": { fontSize: 12, py: 0.3 } }} disabled={pinnedLinkLoading} /><IconButton size="small" onClick={() => removeLinkRow(key, idx)}><CloseIcon sx={{ fontSize: 13 }} /></IconButton></Box>))}<Box sx={{ display: "flex", alignItems: "center", mt: 0.5, gap: 0.5 }}><Typography sx={{ fontSize: 11, color, cursor: "pointer", fontWeight: 600 }} onClick={() => addLinkRow(key)}>+ Add row</Typography><Box sx={{ flex: 1 }} /><Tooltip title="Cancel"><IconButton size="small" onClick={() => setNewLinkRows(prev => ({ ...prev, [key]: null }))}><CloseIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip><IconButton size="small" sx={{ bgcolor: color, color: "#fff", borderRadius: 1, px: 1, py: 0.3 }} onClick={() => handlePinnedLinkAddAll(key)} disabled={pinnedLinkLoading}><SaveIcon sx={{ fontSize: 13, mr: 0.3 }} /><Typography sx={{ fontSize: 11, fontWeight: 700 }}>Save All</Typography></IconButton></Box></Box>)}
                  {links.length === 0 && !isAdding && (<Box sx={{ px: 1.5, py: 0.4 }}><Typography sx={{ fontSize: 11, color: "#aaa", fontStyle: "italic", cursor: "pointer", "&:hover": { color } }} onClick={() => addLinkRow(key)}>+ Add {key === "quota" ? "Quota" : key === "live" ? "Live" : "Test"} Link</Typography></Box>)}
                </>
              )}
            </Box>
          );
        })}
      </Popover>

      {/* QNR / Analysis Plan popovers */}
      {["questionnaire", "analysis_plan"].map((attKey) => {
        const isQNR = attKey === "questionnaire"; const label = isQNR ? "QNR" : "Analysis Plan"; const color = isQNR ? "#4a148c" : "#bf360c"; const bg = isQNR ? "#f3e5f5" : "#fbe9e7"; const border = isQNR ? "#ce93d8" : "#ffab91"; const anchorEl = isQNR ? qnrAnchorEl : apAnchorEl; const setAnchorEl = isQNR ? setQnrAnchorEl : setApAnchorEl; const att = pinnedAttachments[attKey]; const isUploading = attachUploading[attKey];
        return (
          <Popover key={attKey} open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: "top", horizontal: "left" }} transformOrigin={{ vertical: "bottom", horizontal: "left" }} disableRestoreFocus PaperProps={{ sx: { width: 320, p: 1.5 } }}>
            <Box sx={{ border: `1px solid ${border}`, borderRadius: "7px", background: bg, overflow: "hidden" }}>
              <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.6 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color, flex: 1 }}>{isQNR ? "📋" : "📊"} {label}{att ? ` (v${att.version})` : ""}</Typography>
                <Tooltip title={att ? `Replace ${label}` : `Upload ${label}`}><IconButton size="small" sx={{ p: 0.3, color }} onClick={() => attachFileRefs.current[attKey]?.click()}><Typography sx={{ fontSize: 16, lineHeight: 1, fontWeight: 700 }}>↑</Typography></IconButton></Tooltip>
              </Box>
              <Box sx={{ px: 1.5, py: 0.6, borderTop: `1px solid ${border}` }}>
                {isUploading ? (<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><CircularProgress size={12} sx={{ color }} /><Typography sx={{ fontSize: 11, color }}>Uploading…</Typography></Box>)
                  : att ? (<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Box sx={{ flex: 1, overflow: "hidden" }}><Typography sx={{ fontSize: 11, color, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.original_filename}</Typography><Typography sx={{ fontSize: 10, color: "#888" }}>v{att.version} · {att.uploaded_by} · {new Date(att.uploaded_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</Typography></Box><Tooltip title="Download"><IconButton size="small" sx={{ color, p: 0.3 }} component="a" href={att.file_url} download target="_blank" rel="noopener noreferrer"><FileDownloadIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip><Tooltip title="Replace"><IconButton size="small" sx={{ color: "#666", p: 0.3 }} onClick={() => attachFileRefs.current[attKey]?.click()}><EditIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip><Tooltip title="Remove"><IconButton size="small" sx={{ color: "#c62828", p: 0.3 }} onClick={() => handleAttachmentDelete(attKey)}><DeleteIcon sx={{ fontSize: 13 }} /></IconButton></Tooltip></Box>)
                  : (<Typography sx={{ fontSize: 11, color: "#aaa", fontStyle: "italic", cursor: "pointer", "&:hover": { color } }} onClick={() => attachFileRefs.current[attKey]?.click()}>+ Click to upload {label}</Typography>)}
              </Box>
            </Box>
          </Popover>
        );
      })}

      {/* Status menu */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={() => setStatusMenuAnchor(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }} transformOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {STATUS_OPTIONS.map(option => (
          <MenuItem key={option.value} selected={option.value === statusMenuCurrentStatus} onClick={() => handleStatusSelect(option.value)} sx={{ fontSize: "0.85rem", minHeight: 32 }}>
            <Chip label={option.label} color={option.color} size="small" variant={option.value === statusMenuCurrentStatus ? "filled" : "outlined"} sx={{ pointerEvents: "none", height: 22, fontSize: "0.75rem" }} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default JTDiscussionPage;
